// eslint-disable-next-line wix-editor/no-internal-import
import {STYLE, DATA} from '@wix/component-protocol/schema'
import type {Builder} from '@wix/document-services-types'
import slotsSchemas from './slotsSchemas.json'
import dataSchemas from './dataSchemas.json'
import {pick, mapValues, forOwn, isEmpty, pickBy, reduce, omit} from 'lodash'
import {type DeriveFromManifestConfig, topLevelDataNodeProperties, topLevelStyleNodeProperties} from './manifest'
import {CSS_SHORTHAND_MAPPINGS} from './shorthandMappings'

export type Schema = Record<string, any> | boolean

type ValidationDetails = Record<string, any>

type SpecialHandledDataTypes =
    | 'UNKNOWN_DataType'
    | 'schema'
    | 'text'
    | 'textEnum'
    | 'link'
    | 'a11y'
    | 'arrayItems'
    | 'container'

type SpecialDataCaseHandler = (details: ValidationDetails, config?: DeriveFromManifestConfig) => Schema

type SpecialDataCaseHandlers = Partial<Record<SpecialHandledDataTypes, SpecialDataCaseHandler>>

type StyleSchema = string

type CssVariableType = keyof Omit<typeof STYLE.CSS_VARIABLE_TYPE, 'UNKNOWN_CssDataType' | 'customEnum'>
type StaticCssPropertyType = keyof Omit<
    typeof STYLE.CSS_PROPERTY_TYPE,
    'UNKNOWN_CssPropertyType' | 'textIndent' | keyof CssVariableType
>
type DynamicCssPropertyType =
    | keyof Pick<typeof STYLE.CSS_PROPERTY_TYPE, 'writingMode' | 'display' | 'filter' | 'backdropFilter'>
    | keyof Pick<typeof STYLE.CSS_VARIABLE_TYPE, 'customEnum'>

const dataTypeSchemas: Partial<Record<NonNullable<Builder.DataType>, Schema>> = dataSchemas

const specialDataCaseHandlers: SpecialDataCaseHandlers = {
    [DATA.DATA_TYPE.UNKNOWN_DataType]: () => {
        return false
    },
    [DATA.DATA_TYPE.schema]: details => {
        return details
    },
    [DATA.DATA_TYPE.text]: details => {
        const maxLength = details?.maxLength && details.maxLength < 4000 ? details.maxLength : 4000
        return {type: 'string', ...details, maxLength}
    },
    [DATA.DATA_TYPE.textEnum]: ({options}) => {
        return {
            type: 'string',
            enum: options.map(({value}: any) => value),
            maxLength: 4000
        }
    },
    [DATA.DATA_TYPE.link]: details => {
        const allowedLinkTypes: string[] = details?.linkTypes ?? Object.keys(dataSchemas.link)
        return {
            anyOf: allowedLinkTypes.map(linkType => dataSchemas.link[linkType as keyof typeof dataSchemas.link])
        }
    },
    [DATA.DATA_TYPE.a11y]: ({attributes}) => {
        return {
            type: 'object',
            additionalProperties: false,
            properties: pick(dataSchemas.a11y, attributes)
        }
    },
    [DATA.DATA_TYPE.arrayItems]: (details, config) => {
        if (details.dataItem) {
            const {dataItem} = details
            const extraValidationParameters = dataItem.dataType ? dataItem[dataItem.dataType] : {}
            return {
                type: 'array',
                items: getDataTypeSchema(dataItem.dataType, extraValidationParameters, config),
                maxItems: details.maxSize
            }
        }

        if (details.data?.items) {
            const properties = mapValues(details.data.items, dataItem => {
                const {dataType} = dataItem
                const extraValidationParameters = dataType ? dataItem[dataType] : {}
                return getDataTypeSchema(dataType, extraValidationParameters, config)
            })

            return {
                type: 'array',
                items: {
                    type: 'object',
                    properties,
                    additionalProperties: false
                },
                maxItems: details.maxSize
            }
        }

        return false
    },
    [DATA.DATA_TYPE.container]: (_, config) => {
        if (config && !config.hasDynamicContainers) {
            // If we have a container within an array, we need to set the hasDynamicContainers flag to true as side effect (within inner elements as well)
            config.hasDynamicContainers = true
        }

        return {
            type: 'string',
            pseudoType: ['weakRef'],
            referencedMap: 'slots',
            refTypes: [],
            shouldCollect: false,
            shouldValidate: false,
            isTopLevelDataNode: false
        }
    }
}

const referencedFromCssPropertySchemas = (schemaName: string): string =>
    `urn:dm:schemas:cssPropertySchemas.json#/definitions/${schemaName}`

function mapTypesToCssPropertySchema<T extends Record<string, string>>(types: T): Record<keyof T, string> {
    return mapValues(types, referencedFromCssPropertySchemas)
}

const cssVariableTypeToSchema = mapTypesToCssPropertySchema<Record<CssVariableType, StyleSchema>>({
    [STYLE.CSS_VARIABLE_TYPE.color]: 'CSS.Color',
    [STYLE.CSS_VARIABLE_TYPE.blendMode]: 'CSS.BlendMode',
    [STYLE.CSS_VARIABLE_TYPE.angle]: 'CSS.Angle',
    [STYLE.CSS_VARIABLE_TYPE.lengthPercentage]: 'CSS.LengthPercentage',
    [STYLE.CSS_VARIABLE_TYPE.length]: 'CSS.Length',
    [STYLE.CSS_VARIABLE_TYPE.number]: 'CSS.Number',
    [STYLE.CSS_VARIABLE_TYPE.percentage]: 'CSS.Percentage',
    [STYLE.CSS_VARIABLE_TYPE.string]: 'CSS.String',
    [STYLE.CSS_VARIABLE_TYPE.time]: 'CSS.Time'
})

const cssPropertyTypes = mapTypesToCssPropertySchema<Record<StaticCssPropertyType, StyleSchema>>({
    [STYLE.CSS_PROPERTY_TYPE.color]: 'CSS.Color',
    [STYLE.CSS_PROPERTY_TYPE.borderRightColor]: 'CSS.Color',
    [STYLE.CSS_PROPERTY_TYPE.borderLeftColor]: 'CSS.Color',
    [STYLE.CSS_PROPERTY_TYPE.borderBottomColor]: 'CSS.Color',
    [STYLE.CSS_PROPERTY_TYPE.borderTopColor]: 'CSS.Color',
    [STYLE.CSS_PROPERTY_TYPE.borderTopStyle]: 'CSS.BorderTopStyle',
    [STYLE.CSS_PROPERTY_TYPE.borderBottomStyle]: 'CSS.BorderTopStyle',
    [STYLE.CSS_PROPERTY_TYPE.borderLeftStyle]: 'CSS.BorderTopStyle',
    [STYLE.CSS_PROPERTY_TYPE.borderRightStyle]: 'CSS.BorderTopStyle',
    [STYLE.CSS_PROPERTY_TYPE.borderTopWidth]: 'CSS.BorderTopWidth',
    [STYLE.CSS_PROPERTY_TYPE.borderBottomWidth]: 'CSS.BorderTopWidth',
    [STYLE.CSS_PROPERTY_TYPE.borderLeftWidth]: 'CSS.BorderTopWidth',
    [STYLE.CSS_PROPERTY_TYPE.borderRightWidth]: 'CSS.BorderTopWidth',
    [STYLE.CSS_PROPERTY_TYPE.borderWidth]: 'CSS.BorderWidth',
    [STYLE.CSS_PROPERTY_TYPE.borderStyle]: 'CSS.BorderStyle',
    [STYLE.CSS_PROPERTY_TYPE.borderColor]: 'CSS.BorderColor',
    [STYLE.CSS_PROPERTY_TYPE.borderRight]: 'CSS.Border',
    [STYLE.CSS_PROPERTY_TYPE.borderLeft]: 'CSS.Border',
    [STYLE.CSS_PROPERTY_TYPE.borderTop]: 'CSS.Border',
    [STYLE.CSS_PROPERTY_TYPE.borderBottom]: 'CSS.Border',
    [STYLE.CSS_PROPERTY_TYPE.border]: 'CSS.Border',
    [STYLE.CSS_PROPERTY_TYPE.borderTopLeftRadius]: 'CSS.BorderTopLeftRadius',
    [STYLE.CSS_PROPERTY_TYPE.borderTopRightRadius]: 'CSS.BorderTopLeftRadius',
    [STYLE.CSS_PROPERTY_TYPE.borderBottomLeftRadius]: 'CSS.BorderTopLeftRadius',
    [STYLE.CSS_PROPERTY_TYPE.borderBottomRightRadius]: 'CSS.BorderTopLeftRadius',
    [STYLE.CSS_PROPERTY_TYPE.borderRadius]: 'CSS.BorderRadius',
    [STYLE.CSS_PROPERTY_TYPE.font]: 'CSS.Font',
    [STYLE.CSS_PROPERTY_TYPE.fontFamily]: 'CSS.FontFamily',
    [STYLE.CSS_PROPERTY_TYPE.fontSize]: 'CSS.FontSize',
    [STYLE.CSS_PROPERTY_TYPE.fontStretch]: 'CSS.FontStretch',
    [STYLE.CSS_PROPERTY_TYPE.fontStyle]: 'CSS.FontStyle',
    [STYLE.CSS_PROPERTY_TYPE.fontVariant]: 'CSS.FontVariant',
    [STYLE.CSS_PROPERTY_TYPE.fontWeight]: 'CSS.FontWeight',
    [STYLE.CSS_PROPERTY_TYPE.lineHeight]: 'CSS.LineHeight',
    [STYLE.CSS_PROPERTY_TYPE.letterSpacing]: 'CSS.LetterSpacing',
    [STYLE.CSS_PROPERTY_TYPE.textAlign]: 'CSS.TextAlign',
    [STYLE.CSS_PROPERTY_TYPE.textTransform]: 'CSS.TextTransform',
    [STYLE.CSS_PROPERTY_TYPE.textShadow]: 'CSS.TextShadow',
    [STYLE.CSS_PROPERTY_TYPE.textDecoration]: 'CSS.TextDecoration',
    [STYLE.CSS_PROPERTY_TYPE.textDecorationColor]: 'CSS.Color',
    [STYLE.CSS_PROPERTY_TYPE.textDecorationLine]: 'CSS.TextDecorationLine',
    [STYLE.CSS_PROPERTY_TYPE.textDecorationStyle]: 'CSS.TextDecorationStyle',
    [STYLE.CSS_PROPERTY_TYPE.textDecorationThickness]: 'CSS.TextDecorationThickness',
    [STYLE.CSS_PROPERTY_TYPE.textOverflow]: 'CSS.TextOverflow',
    [STYLE.CSS_PROPERTY_TYPE.padding]: 'CSS.Padding',
    [STYLE.CSS_PROPERTY_TYPE.paddingTop]: 'CSS.PaddingTop',
    [STYLE.CSS_PROPERTY_TYPE.paddingBottom]: 'CSS.PaddingTop',
    [STYLE.CSS_PROPERTY_TYPE.paddingLeft]: 'CSS.PaddingTop',
    [STYLE.CSS_PROPERTY_TYPE.paddingRight]: 'CSS.PaddingTop',
    [STYLE.CSS_PROPERTY_TYPE.margin]: 'CSS.Margin',
    [STYLE.CSS_PROPERTY_TYPE.marginTop]: 'CSS.MarginTop',
    [STYLE.CSS_PROPERTY_TYPE.marginBottom]: 'CSS.MarginTop',
    [STYLE.CSS_PROPERTY_TYPE.marginLeft]: 'CSS.MarginTop',
    [STYLE.CSS_PROPERTY_TYPE.marginRight]: 'CSS.MarginTop',
    [STYLE.CSS_PROPERTY_TYPE.boxShadow]: 'CSS.BoxShadow',
    [STYLE.CSS_PROPERTY_TYPE.opacity]: 'CSS.Opacity',
    [STYLE.CSS_PROPERTY_TYPE.background]: 'CSS.Background',
    [STYLE.CSS_PROPERTY_TYPE.backgroundSize]: 'CSS.BackgroundSize',
    [STYLE.CSS_PROPERTY_TYPE.backgroundColor]: 'CSS.Color',
    [STYLE.CSS_PROPERTY_TYPE.backgroundImage]: 'CSS.BackgroundImage',
    [STYLE.CSS_PROPERTY_TYPE.alignSelf]: 'CSS.AlignSelf',
    [STYLE.CSS_PROPERTY_TYPE.width]: 'CSS.HeightOrWidth',
    [STYLE.CSS_PROPERTY_TYPE.height]: 'CSS.HeightOrWidth',
    [STYLE.CSS_PROPERTY_TYPE.backgroundClip]: 'CSS.BackgroundClip',
    [STYLE.CSS_PROPERTY_TYPE.backgroundOrigin]: 'CSS.BackgroundOrigin',
    [STYLE.CSS_PROPERTY_TYPE.backgroundPosition]: 'CSS.BackgroundPosition',
    [STYLE.CSS_PROPERTY_TYPE.backgroundRepeat]: 'CSS.BackgroundRepeat',
    [STYLE.CSS_PROPERTY_TYPE.backgroundAttachment]: 'CSS.BackgroundAttachment',
    [STYLE.CSS_PROPERTY_TYPE.marginInlineStart]: 'CSS.MarginTop',
    [STYLE.CSS_PROPERTY_TYPE.marginInlineEnd]: 'CSS.MarginTop',
    [STYLE.CSS_PROPERTY_TYPE.paddingInlineStart]: 'CSS.PaddingTop',
    [STYLE.CSS_PROPERTY_TYPE.paddingInlineEnd]: 'CSS.PaddingTop',
    [STYLE.CSS_PROPERTY_TYPE.borderInlineStart]: 'CSS.Border',
    [STYLE.CSS_PROPERTY_TYPE.borderInlineStartColor]: 'CSS.BorderColor',
    [STYLE.CSS_PROPERTY_TYPE.borderInlineStartWidth]: 'CSS.BorderTopWidth',
    [STYLE.CSS_PROPERTY_TYPE.borderInlineStartStyle]: 'CSS.BorderTopStyle',
    [STYLE.CSS_PROPERTY_TYPE.borderInlineEnd]: 'CSS.Border',
    [STYLE.CSS_PROPERTY_TYPE.borderInlineEndColor]: 'CSS.BorderColor',
    [STYLE.CSS_PROPERTY_TYPE.borderInlineEndWidth]: 'CSS.BorderTopWidth',
    [STYLE.CSS_PROPERTY_TYPE.borderInlineEndStyle]: 'CSS.BorderTopStyle',
    [STYLE.CSS_PROPERTY_TYPE.borderStartStartRadius]: 'CSS.BorderTopLeftRadius',
    [STYLE.CSS_PROPERTY_TYPE.borderStartEndRadius]: 'CSS.BorderTopLeftRadius',
    [STYLE.CSS_PROPERTY_TYPE.borderEndStartRadius]: 'CSS.BorderTopLeftRadius',
    [STYLE.CSS_PROPERTY_TYPE.borderEndEndRadius]: 'CSS.BorderTopLeftRadius',
    [STYLE.CSS_PROPERTY_TYPE.overflow]: 'CSS.Overflow',
    [STYLE.CSS_PROPERTY_TYPE.alignItems]: 'CSS.AlignItems',
    [STYLE.CSS_PROPERTY_TYPE.flexDirection]: 'CSS.FlexDirection',
    [STYLE.CSS_PROPERTY_TYPE.gap]: 'CSS.Gap',
    [STYLE.CSS_PROPERTY_TYPE.columnGap]: 'CSS.SingleValueGap',
    [STYLE.CSS_PROPERTY_TYPE.rowGap]: 'CSS.SingleValueGap',
    [STYLE.CSS_PROPERTY_TYPE.justifyContent]: 'CSS.JustifyContent',
    [STYLE.CSS_PROPERTY_TYPE.fill]: 'CSS.FillWithURL',
    [STYLE.CSS_PROPERTY_TYPE.isolation]: 'CSS.Isolation',
    [STYLE.CSS_PROPERTY_TYPE.mixBlendMode]: 'CSS.MixBlendMode',
    [STYLE.CSS_PROPERTY_TYPE.objectFit]: 'CSS.ObjectFit',
    [STYLE.CSS_PROPERTY_TYPE.objectPosition]: 'CSS.ObjectPosition',
    [STYLE.CSS_PROPERTY_TYPE.fillOpacity]: 'CSS.FillOpacity',
    [STYLE.CSS_PROPERTY_TYPE.stroke]: 'CSS.Stroke',
    [STYLE.CSS_PROPERTY_TYPE.strokeWidth]: 'CSS.StrokeWidth',
    [STYLE.CSS_PROPERTY_TYPE.strokeOpacity]: 'CSS.StrokeOpacity',
    [STYLE.CSS_PROPERTY_TYPE.writingMode]: 'CSS.WritingMode',
    [STYLE.CSS_PROPERTY_TYPE.display]: 'CSS.Display',
    [STYLE.CSS_PROPERTY_TYPE.filter]: 'CSS.Filter',
    [STYLE.CSS_PROPERTY_TYPE.backdropFilter]: 'CSS.Filter'
})

type CSSProperties = Builder.ResolvedEditorElement['cssProperties']
type CSSCustomProperties = Builder.ResolvedEditorElement['cssCustomProperties']

interface CSSPropertyDefinition {
    propertyName: string
    styleItem: NonNullable<CSSProperties>[string] & {cssPropertyType?: string}
}

const getShortHandProperties = (propertyName: string): CSSPropertyDefinition[] => {
    // Look up expansions in the static mapping and add them
    const expansions = CSS_SHORTHAND_MAPPINGS[propertyName]
    if (expansions) {
        return expansions.map(({propertyName: propName, cssPropertyType}) => ({
            propertyName: propName,
            styleItem: {cssPropertyType} as Builder.StyleItem
        }))
    }
    return []
}

const cssPropertyToSchema: Record<StaticCssPropertyType | CssVariableType, StyleSchema> = {
    ...cssVariableTypeToSchema,
    ...cssPropertyTypes
}

const isUnsupportedType = (typeName: Builder.StyleType): boolean => {
    return (
        typeName === STYLE.CSS_VARIABLE_TYPE.UNKNOWN_CssDataType ||
        typeName === STYLE.CSS_PROPERTY_TYPE.UNKNOWN_CssPropertyType
    )
}

const isKnownTypeWithoutSchema = (typeName: Builder.StyleType): boolean => {
    return (
        Object.keys(STYLE.CSS_VARIABLE_TYPE).includes(typeName!) ||
        Object.keys(STYLE.CSS_PROPERTY_TYPE).includes(typeName!)
    )
}

export function getSlotsSchema(typeName: Builder.DataType): Schema {
    return slotsSchemas[typeName! as keyof typeof slotsSchemas]
}

export function getDataTypeSchema(
    typeName: Builder.DataType,
    validationDetails: ValidationDetails,
    config?: DeriveFromManifestConfig
): Schema {
    const specialCaseHandler = specialDataCaseHandlers[typeName! as SpecialHandledDataTypes]
    if (specialCaseHandler) {
        return specialCaseHandler(validationDetails, config)
    }
    const schema = dataTypeSchemas[typeName!]
    if (typeof schema === 'boolean') {
        return schema
    }
    if (!schema) {
        throw new Error(`Unsupported data type: ${typeName}`)
    }
    return {...schema, ...validationDetails}
}

const restrictWithEnum = (referencedSchemaType: string, enumValues?: string[]) => {
    const schema = {
        $ref: referencedFromCssPropertySchemas(referencedSchemaType)
    }
    if (enumValues) {
        return {
            allOf: [schema, {type: 'string', enum: enumValues.map(v => v.replace('_', '-'))}]
        }
    }
    return schema
}

const restrictFilterSchema = (referencedSchemaType: string, functionTypes?: string[]) => {
    const schema = {
        $ref: referencedFromCssPropertySchemas(referencedSchemaType)
    }
    if (functionTypes) {
        const allowedOptions = functionTypes.join('|')
        return {
            allOf: [schema, {type: 'string', pattern: `(${allowedOptions})\\(.*\\)(\\s(${allowedOptions})\\(.*\\))*$`}]
        }
    }
    return schema
}
const dynamicCssPropertiesToSchema: Record<DynamicCssPropertyType, (styleItem: Builder.StyleItem) => Schema> = {
    [STYLE.CSS_VARIABLE_TYPE.customEnum]: styleItem => {
        const {customEnum} = styleItem

        const enumValues = customEnum!.options!.map(({value}) => value)
        return {
            type: 'string',
            enum: enumValues
        }
    },
    [STYLE.CSS_PROPERTY_TYPE.filter]: styleItem => {
        const {filter} = styleItem
        return restrictFilterSchema('CSS.Filter', filter?.filterFunctions)
    },
    [STYLE.CSS_PROPERTY_TYPE.backdropFilter]: styleItem => {
        const {backdropFilter} = styleItem
        return restrictFilterSchema('CSS.Filter', backdropFilter?.filterFunctions)
    },
    [STYLE.CSS_PROPERTY_TYPE.writingMode]: styleItem => {
        const {writingMode} = styleItem
        return restrictWithEnum('CSS.WritingMode', writingMode?.writingModeValues)
    },
    [STYLE.CSS_PROPERTY_TYPE.display]: styleItem => {
        const {display} = styleItem
        return restrictWithEnum('CSS.Display', display?.displayValues)
    }
}
const getStyleType = (styleItem: Builder.StyleItem): Builder.StyleType =>
    (styleItem.styleType ?? styleItem.cssPropertyType ?? styleItem.cssVariableType) as unknown as Builder.StyleType

export function getStyleTypeSchema(styleItem: any): Schema {
    const typeName = getStyleType(styleItem)
    if (isUnsupportedType(typeName)) {
        return false
    }

    const generateSchema = dynamicCssPropertiesToSchema[typeName! as keyof typeof dynamicCssPropertiesToSchema]
    if (generateSchema) {
        return generateSchema(styleItem)
    }

    const schemaName = cssPropertyToSchema[typeName! as keyof typeof cssPropertyToSchema]

    if (schemaName) {
        return {$ref: `${schemaName}`}
    }

    if (isKnownTypeWithoutSchema(typeName)) {
        return {type: ['string', 'number']}
    }

    throw new Error(`Unsupported style type: ${typeName}`)
}

export function generateStylePropertiesSchema(cssProperties: CSSProperties, cssCustomProperties: CSSCustomProperties) {
    const styleNamespaces: {
        cssProperties: Record<string, Schema>
        cssCustomProperties: Record<string, Schema>
    } = {
        cssProperties: {},
        cssCustomProperties: {}
    }

    forOwn(cssProperties, (property: NonNullable<CSSProperties>[string], propertyName: string) => {
        const propertiesToUpdate: CSSPropertyDefinition[] = [
            {propertyName, styleItem: {...property, cssPropertyType: propertyName}}
        ]
        const additionalShorthandProperties = getShortHandProperties(propertyName)

        propertiesToUpdate
            .concat(additionalShorthandProperties)
            .forEach(({propertyName: cssPropertyName, styleItem: propertyDefinition}) => {
                const styleTypeSchema = getStyleTypeSchema(propertyDefinition)
                if (styleTypeSchema) {
                    styleNamespaces.cssProperties[cssPropertyName] = styleTypeSchema
                }
            })
    })
    forOwn(cssCustomProperties, (property: NonNullable<CSSCustomProperties>[string], propertyName: string) => {
        styleNamespaces.cssCustomProperties[propertyName] = getStyleTypeSchema(property)
    })
    return mapValues(
        pickBy(styleNamespaces, styleNs => !isEmpty(styleNs)),
        styleNamespace => ({
            type: 'object',
            additionalProperties: false,
            properties: styleNamespace
        })
    )
}

const getStylePropertiesAsV2 = (style: Builder.Style) => {
    return pickBy(
        reduce(
            style,
            (v2Style, styleItem: Builder.StyleItem, propertyName: string) => {
                const cssPropertyType = getStyleType(styleItem)
                const baseStyleItem = omit(styleItem, ['cssPropertyType', 'cssVariableType', 'cssCustomProperty'])
                if (styleItem.cssCustomProperty || cssPropertyType === 'customEnum') {
                    v2Style.cssCustomProperties[propertyName] = {
                        ...omit(styleItem, ['cssPropertyType', 'cssVariableType', 'cssCustomProperty']),
                        cssPropertyType
                    }
                } else {
                    const actualPropertyName = cssPropertyType as StaticCssPropertyType
                    v2Style.cssProperties[actualPropertyName] = baseStyleItem
                }
                return v2Style
            },
            {
                cssProperties: {},
                cssCustomProperties: {}
            }
        ),
        props => !isEmpty(props)
    ) as Pick<Builder.ResolvedEditorElement, 'cssProperties' | 'cssCustomProperties'>
}
export function generateStyleSchema(editorElement: Builder.ResolvedEditorElement, isDraftSchema: boolean): Schema {
    const {cssProperties, cssCustomProperties, style} = editorElement ?? {}
    let resolvedCssProperties = cssProperties
    let resolvedCssCustomProperties = cssCustomProperties
    if (!(resolvedCssProperties || resolvedCssCustomProperties) && style) {
        const styleProperties = getStylePropertiesAsV2(style)
        resolvedCssProperties = styleProperties.cssProperties
        resolvedCssCustomProperties = styleProperties.cssCustomProperties
    }
    const stylePropertiesSchema = generateStylePropertiesSchema(resolvedCssProperties, resolvedCssCustomProperties)
    return {
        type: 'object',
        additionalProperties: false,
        isTopLevelDataNode: true,
        properties: {
            ...topLevelDataNodeProperties(),
            ...topLevelStyleNodeProperties(),
            name: {
                type: 'string',
                maxLength: 100
            },
            ...stylePropertiesSchema
        },
        isDraftSchema
    }
}
